import { z } from 'zod';

import { zFirestampOptional } from '@elara/firebolt/zod-types';

import { FileAttachmentOrStringSchema } from './attachment-schemas.js';
import {
  BookingAddressSchema,
  BookingChannelSchema,
  FlightBookingProviderSchema,
  LeadGuestSchema,
  PriceSnapshotItemSchema,
  SelectedRoomExtraSchema,
} from './booking-schemas.js';
import {
  AdditionalDetailSchema,
  CancellationPolicySchema,
  DepositPolicySchema,
  GuaranteePolicySchema,
  RoomSelectionSchema,
} from './room-schemas.js';
import { BasicPersonNameSchema, CostSchema, ItemActorSchema } from './shared-schemas.js';

/**
 * The booking-channel enum lives beside the checkout-room schema it is shared with
 * (`HotelBookingRoomSchema.channel`); re-exported here because the itinerary item is where most
 * readers meet it.
 */
export {
  AUTOMATED_BOOKING_CHANNELS,
  type BookingChannel,
  BookingChannelSchema,
  isAutomatedBookingChannel,
} from './booking-schemas.js';

/**
 * Arbitrary key-value object schema that serializes with a non-empty `properties` key
 * so Gemini/Genkit converters (which assume object schemas have property.properties) do not crash.
 * Use instead of z.record(z.string(), z.unknown()) when the schema is passed to Genkit tools.
 */
const RecordUnknownSchema = z
  .object({ _: z.optional(z.string()).describe('Reserved; arbitrary keys allowed') })
  .catchall(z.unknown());

export const BookingStatusSchema = z.enum([
  'planned',
  'approved',
  'booked',
  // Non-terminal: a LIVE Sabre cancel that did not read-back-verify the segment is gone. The reservation
  // (and any no-show/deposit charge) may still be live, so we never claim terminal 'cancelled' for it — an
  // operator (or the reconciler) resolves it to 'cancelled' once Sabre confirms the segment is gone (H2).
  'cancellation_pending',
  'cancelled',
]);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

/**
 * Server-denormalized bookability verdict for the linked option (a room rate or
 * a flight), written whenever that option is re-priced (`RecheckRoomPrice` /
 * `RecheckFlightPrice`). Collapses the live `RoomOption.rates` /
 * `FlightOption.isAvailable` two-doc join — previously re-implemented per client
 * — into a single value every client reads. Absent on items that predate the
 * denormalization or haven't been rechecked; clients fall back to the live join
 * in that case. The submit-time PriceCheck remains the hard gate.
 */
export const ItemAvailabilityStateSchema = z.enum(['available', 'unavailable']);
export type ItemAvailabilityState = z.infer<typeof ItemAvailabilityStateSchema>;

/**
 * A traveller associated with a booking (name + date of birth for hotel check-in).
 */
export const BookingTravellerSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string().optional(),
});

/**
 * Which Travelport created the reservation — `pre-production` (host `api.pp.travelport.net`) makes
 * test bookings, not stays. This is the VENDOR's environment, read off the API host at create time,
 * not Elara's deploy tier: a preview deploy or a laptop can point at either Travelport, so labelling
 * it "preview" / "staging" would misstate what was actually booked.
 */
export const BookingEnvironmentSchema = z.enum(['production', 'pre-production']);
export type BookingEnvironment = z.infer<typeof BookingEnvironmentSchema>;

/**
 * One line in a hotel rate's payment schedule. Computed at submit time from
 * the rate's deposit policies and persisted per itinerary item so the trip
 * and admin views can render the same breakdown as the checkout summary.
 *
 * `kind: 'at_booking'` collapses deposits whose resolved due date is today
 * or earlier; `'balance'` is the remainder due later. Display labels are
 * derived from these structural fields at render time via
 * `formatPaymentScheduleLabel` (no label is stored on the doc so copy can
 * be tweaked without re-writing historical data).
 */
export const PaymentScheduleEntrySchema = z.object({
  kind: z.enum(['at_booking', 'balance']),
  amount: z.number(),
  currency: z.string(),
  /** Percentage of the room total this entry represents, when the policy was percent-based. */
  percent: z.number().optional(),
  /** ISO YYYY-MM-DD date this charge is due. Absent for "due at the property" lines. */
  dueDate: z.string().optional(),
});

export type PaymentScheduleEntry = z.infer<typeof PaymentScheduleEntrySchema>;

/**
 * Booking details for an itinerary item — the single source of truth for
 * everything a checkout submission needs to persist. Each item carries its own
 * `booking` (Sabre books one room at a time, the user reports flights
 * one-at-a-time, etc.). Items that went through the same checkout submission
 * share a `bookingGroupId` on the parent itinerary item.
 */
export const BookingDetailsSchema = z.object({
  // Lifecycle
  status: BookingStatusSchema.default('planned'),
  bookedAt: zFirestampOptional().describe('When the booking was submitted'),
  cancelledAt: zFirestampOptional().describe('When the booking was cancelled'),
  cancelledReason: z.string().optional().describe('Why the booking was cancelled (user-supplied or system)'),

  // Channel / system tracking
  channel: BookingChannelSchema.optional(),
  reference: z.string().optional().describe('Booking system reference (the Sabre or Travelport PNR locator)'),
  bookingEnvironment: BookingEnvironmentSchema.optional().describe(
    'Which supplier environment created the reservation (Travelport bookings only): pre-production bookings are test bookings, not real stays',
  ),
  provider: z.string().optional().describe('Display name of the provider the user booked with (e.g. "Skyscanner")'),
  confirmationNumber: z.string().optional().describe('Booking confirmation number'),
  cancellationPolicy: z.string().optional().describe('Cancellation policy details'),
  // Structured policy snapshot (ELARA-771) — the agreed cancellation/deposit/guarantee terms frozen from
  // the volatile rate at submit. `cancellationPolicy` above is the human-readable prose; these are the
  // machine-readable contract the cancel flow, deadline UI (ELARA-788), and disputes read instead of the
  // live (re-priced) rate.
  cancellationPolicies: z
    .array(CancellationPolicySchema)
    .optional()
    .describe('Structured cancellation policy frozen from the rate at submit'),
  depositPolicies: z
    .array(DepositPolicySchema)
    .optional()
    .describe('Structured deposit policy frozen from the rate at submit'),
  guaranteePolicy: GuaranteePolicySchema.optional().describe('Structured guarantee policy frozen from the rate at submit'),
  // Verbatim Sabre rate disclosures (prepay / cancellation / rate-disclaimer text, e.g. "INCLUDES TAXES
  // AND SURCHARGES", "CANCELS ALWAYS CHARGED…") frozen at submit. The GDS source-of-truth behind the
  // structured policies above, preserved for the disclosure record and dispute defense (ELARA-829).
  additionalDetails: z
    .array(AdditionalDetailSchema)
    .optional()
    .describe('Verbatim Sabre rate disclosures frozen from the rate at submit'),

  // Replay-safety carried from the client
  idempotencyKey: z.string().optional().describe('Per-item replay key from the checkout submission'),

  // Customer-facing fields applied to every item in the same checkout group
  leadGuest: LeadGuestSchema.optional().describe('Named guest captured at checkout; duplicated across items in a group'),
  address: BookingAddressSchema.optional().describe('Billing address captured at checkout'),

  // Pricing
  priceSnapshot: PriceSnapshotItemSchema.optional().describe('What the user agreed to pay at submit time'),
  paymentSchedule: z
    .array(PaymentScheduleEntrySchema)
    .optional()
    .describe("When this room will be charged and how much, derived from the rate's deposit policies at submit time"),
  combinedPaymentSchedule: z
    .array(PaymentScheduleEntrySchema)
    .optional()
    .describe('Trip-level payment schedule folded across every room in the booking group, computed at submit. Denormalized identically onto each item in the group so any one item carries the full cashflow view.'),
  actualCost: CostSchema.optional().describe('Final price the user actually paid (flights)'),

  // Payment
  paymentMethodId: z
    .string()
    .nullish()
    .describe('Saved wallet card (users/{uid}/paymentMethods/{id}) used to book; null when a one-off unsaved card was used'),
  // Card identity snapshot frozen at submit so the booking record survives the wallet card being deleted —
  // `paymentMethodId` is only a pointer that can dangle. No PAN/CVV; just the safe display metadata (ELARA-829).
  card: z
    .object({
      last4: z.string().optional(),
      brand: z.string().optional(),
      expMonth: z.number().optional(),
      expYear: z.number().optional(),
    })
    .optional()
    .describe('Safe card metadata captured at submit (last4/brand/expiry) — survives wallet-card deletion'),

  // Hotel-specific
  sabreBookingKey: z.string().optional().describe('Per-room Sabre booking key for fulfilment'),
  selectedExtras: z.array(SelectedRoomExtraSchema).optional().describe('Crib/rollaway/etc. selected at checkout'),
  bedTypePreference: z
    .number()
    .optional()
    .describe('Sabre bed-type code the guest requested at checkout (matches RoomOption.bedTypes)'),
  // Authoritative stay dates frozen at submit (ELARA-867). These mirror what Sabre actually booked, so
  // booked views render them instead of the (mutable) itinerary item dates. Absent on bookings that
  // predate the freeze and on external/manual bookings (those fall back to item dates).
  checkInDate: z.string().optional().describe('Confirmed check-in date (YYYY-MM-DD) frozen from the booked rate at submit'),
  checkOutDate: z.string().optional().describe('Confirmed check-out date (YYYY-MM-DD) frozen from the booked rate at submit'),

  // Flight-specific
  bookedProvider: FlightBookingProviderSchema.optional().describe('External provider where the user booked the flight'),

  // Admin / management
  managedByElara: z.boolean().optional().describe("True when Elara is fulfilling the booking on the user's behalf"),
  customerRequest: z
    .string()
    .optional()
    .describe(
      'Something the customer asked for on this booking, in their words — e.g. "high floor, away from the lift" or "celebrating an anniversary". Requests only: never facts about the booking itself (price, points used, passenger names, ticket type), which belong in their own fields or nowhere.',
    ),
  advisorNote: z.string().optional().describe('Travel advisor note visible to the customer'),
  contactPhone: z.string().optional().describe('Contact phone number for the booking'),
  internalNotes: z.string().optional().describe('Admin scratch notes (not user-visible)'),

  /** @deprecated No longer written. Kept on the schema so older docs still parse. */
  travellers: z.array(BookingTravellerSchema).optional().describe('Deprecated; legacy travellers field, not written by any current flow'),
});

/**
 * Partial update schema for booking details.
 * No defaults to avoid implicit field materialization during patch updates.
 */
export const BookingDetailsUpdateSchema = z.object({
  status: BookingStatusSchema.optional(),
  paymentMethodId: z.string().nullish(),
  bookedAt: zFirestampOptional(),
  cancelledAt: zFirestampOptional(),
  cancelledReason: z.string().optional(),
  channel: BookingChannelSchema.optional(),
  reference: z.string().optional(),
  bookingEnvironment: BookingEnvironmentSchema.optional(),
  provider: z.string().optional().describe('Display name of the provider the user booked with'),
  // Nullish (not just optional): booking updates DEEP-MERGE onto the stored map, so key
  // omission means "leave unchanged" — an explicit null is the only way to CLEAR a field
  // (the flow strips null-valued keys from the merged map before writing).
  confirmationNumber: z.string().nullish().describe('Booking confirmation number; null clears it'),
  cancellationPolicy: z.string().optional().describe('Cancellation policy details'),
  cancellationPolicies: z.array(CancellationPolicySchema).optional(),
  depositPolicies: z.array(DepositPolicySchema).optional(),
  guaranteePolicy: GuaranteePolicySchema.optional(),
  idempotencyKey: z.string().optional(),
  leadGuest: LeadGuestSchema.optional(),
  address: BookingAddressSchema.optional(),
  priceSnapshot: PriceSnapshotItemSchema.optional(),
  paymentSchedule: z.array(PaymentScheduleEntrySchema).optional(),
  combinedPaymentSchedule: z.array(PaymentScheduleEntrySchema).optional(),
  actualCost: CostSchema.nullish().describe('Price actually paid; null clears it'),
  sabreBookingKey: z.string().optional(),
  selectedExtras: z.array(SelectedRoomExtraSchema).optional(),
  bedTypePreference: z.number().optional(),
  checkInDate: z.string().optional(),
  checkOutDate: z.string().optional(),
  bookedProvider: FlightBookingProviderSchema.optional(),
  managedByElara: z.boolean().optional(),
  customerRequest: z.string().nullish().describe('Client ask in their words; null clears it'),
  advisorNote: z.string().optional(),
  contactPhone: z.string().optional(),
  internalNotes: z.string().optional(),

  /** @deprecated No longer written. Kept on the schema so older docs still parse. */
  travellers: z.array(BookingTravellerSchema).optional(),
});

/**
 * Base fields common to all itinerary items
 */
const BaseItineraryItemSchema = z.object({
  id: z.string().optional(), // Document ID, optional for creation

  title: z.string().describe('Title of the itinerary item'),

  // Reference to the original entity (roomOptionId, flightId, etc.)
  referenceId: z.string().nullish().describe('Optional ID of the referenced entity (roomOptionId, flightId, etc.)'),

  // Date/time handling with timezone support
  startDateTime: z.string().describe('ISO datetime string with timezone (e.g., "2024-01-15T14:30:00-05:00")'),
  endDateTime: z.string().nullish().describe('ISO datetime string with timezone for multi-day items'),

  addedBy: ItemActorSchema.optional().describe('Who added the item; absent on items saved before this was recorded'),
  updatedBy: ItemActorSchema.optional().describe('Who last changed the item; absent until its first edit'),

  // Soft delete support
  deleted: z.boolean().optional().describe('Whether the item has been soft deleted'),
  deletedAt: z.string().nullish().describe('ISO timestamp for when item was deleted (soft delete)'),

  // Booking state for this item — the single source of truth, written by the
  // checkout flows (Hotel / Flight / Generic). Items submitted in the same
  // checkout share a `bookingGroupId` below.
  booking: BookingDetailsSchema.optional(),

  // Optional correlation key marking items that went through the same checkout
  // submission. Sabre books one room at a time and flights are reported one at
  // a time, so each item carries its own booking state on `booking`; this id
  // just preserves the "they checked out together" relationship for grouped
  // emails / receipts / admin display. Not a foreign key — there is no
  // separate group document.
  bookingGroupId: z.string().nullish().describe('Optional id shared by items submitted in the same checkout'),

  // Server-denormalized bookability verdict for the linked option, refreshed on
  // each recheck. Lets every client gate checkout entry on one server-computed
  // value instead of re-joining the live RoomOption/FlightOption doc.
  availabilityState: ItemAvailabilityStateSchema.nullish().describe(
    'Server-computed availability verdict for the linked room rate / flight',
  ),

  // Notes and attachments
  notes: z.string().nullish().describe('Notes about the itinerary item'),
  attachments: z.array(FileAttachmentOrStringSchema).nullish().describe('File attachments (structured or legacy URL strings)'),

  // Deprecated fields
  referenceData: RecordUnknownSchema.nullish().describe('Snapshot of key data at time of saving'),

  // Type-specific metadata; room branch overrides with RoomSelectionSchema. Null allowed in output (e.g. flight items).
  metadata: RecordUnknownSchema.nullish().describe(
    'Required for room (productCode + occupancy); may omit for flight/custom',
  ),
});

/**
 * Type-specific detail schemas for reference and validation
 * These can be used for validating details objects when needed
 */

export const PassengerSchema = BasicPersonNameSchema.extend({
  dateOfBirth: z.string().optional(),
  passportNumber: z.string().optional(),
  knownTravelerNumber: z.string().optional(),
  seatPreference: z.string().optional(),
});

export const ExperienceDetailsSchema = z.object({
  experienceId: z.string().optional(),
  experienceType: z.string().optional(),
  provider: z.string().optional(),
  duration: z.number().optional(),
  meetingPoint: z.string().optional(),
  participants: z.number().optional(),
  specialRequests: z.string().optional(),
});

/**
 * Proper Zod discriminated union schema
 * This validates the type field and ensures referenceData (and metadata for room) match the expected schema
 */
export const ItineraryItemSchema = z.discriminatedUnion('type', [
  BaseItineraryItemSchema.extend({
    type: z.literal('room'),
    referenceData: RecordUnknownSchema.nullish(),
    metadata: RoomSelectionSchema.describe('Selected rate and occupancy for this room line'),
  }),
  BaseItineraryItemSchema.extend({
    type: z.enum(['flight', 'activity', 'custom', 'place']),
    referenceData: RecordUnknownSchema.nullish(),
    metadata: RecordUnknownSchema.nullish(),
  }),
]);

/**
 * Base schema for Firestore model spec compatibility and partial updates.
 * This extracts the common fields without type discrimination.
 *
 * **Metadata field:** In outputs, `metadata` is type-dependent. For items with
 * `type: 'room'` it is `RoomSelection` (selectedRateKey, occupancy); for
 * `flight` and `custom` it is optional generic key-value. Consumers should
 * narrow by `type` and cast `metadata` when accessing room-specific fields
 * (e.g. `item.type === 'room' && item.metadata` as `RoomSelection`).
 */
export const ItineraryItemBaseSchema = BaseItineraryItemSchema.extend({
  type: z.enum(['flight', 'room', 'activity', 'custom', 'place']),
});

/**
 * Partial update schema used by flows/tools to avoid Zod v4 default injection
 * from `.partial()` on schemas that contain defaults.
 */
export const ItineraryItemUpdateSchema = z.object({
  title: z.string().optional().describe('Title of the itinerary item'),
  referenceId: z.string().nullish().describe('ID of the referenced entity (roomOptionId, flightId, etc.)'),
  startDateTime: z.string().optional().describe('ISO datetime string with timezone'),
  endDateTime: z.string().nullish().describe('ISO datetime string with timezone for multi-day items'),
  deleted: z.boolean().optional().describe('Whether the item has been soft deleted'),
  deletedAt: z.string().nullish().describe('ISO timestamp when item was deleted (soft delete)'),
  booking: BookingDetailsUpdateSchema.optional(),
  bookingGroupId: z.string().nullish().describe('Optional id shared by items submitted in the same checkout'),
  notes: z.string().nullish().describe('Notes about the itinerary item'),
  attachments: z.array(FileAttachmentOrStringSchema).optional().describe('File attachments (structured or legacy URL strings)'),
  referenceData: RecordUnknownSchema.optional().describe('Snapshot of key data at time of saving'),
  metadata: RecordUnknownSchema.optional().describe(
    'Required for room (productCode + occupancy); may omit for flight/activity',
  ),
  type: z.enum(['flight', 'room', 'activity', 'custom', 'place']).optional(),
});

/**
 * Output shape for ListItineraryItems (and any flow returning toClientJSON()).
 * Base item plus server-added displayTitle and timestamps so the implementation return type satisfies the spec.
 */
export const ListItineraryItemOutputSchema = ItineraryItemBaseSchema.extend({
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});

/**
 * Input schema for AddItineraryItem flow: same as base but without title
 * (server generates a title except for custom items, which may pass an
 * optional title) and start/end dates optional (server resolves from
 * flight/room when omitted).
 */
const AddItineraryItemInputBaseSchema = BaseItineraryItemSchema.omit({ title: true, addedBy: true, updatedBy: true }).extend({
  startDateTime: z.string().optional().describe('Resolved from flight/room when omitted'),
  endDateTime: z.string().optional().describe('Resolved from flight/room when omitted'),
});

/**
 * Discriminated union for AddItineraryItem flow input (optional dates allowed).
 * Metadata is required only for room (selectedRateKey, occupancy); for flight it
 * is optional; for custom it is optional and the only type that may pass a
 * title.
 *
 * NOTE: The 'activity' branch is kept for backward compatibility with existing
 * Firestore documents. New writes should use 'custom'.
 */
export const AddItineraryItemInputItemSchema = z.discriminatedUnion('type', [
  AddItineraryItemInputBaseSchema.extend({
    type: z.literal('room'),
    referenceData: RecordUnknownSchema.optional(),
    metadata: RoomSelectionSchema.describe('Selected rate and occupancy for this room line'),
  }),
  AddItineraryItemInputBaseSchema.extend({
    type: z.literal('flight'),
    referenceData: RecordUnknownSchema.optional(),
    metadata: RecordUnknownSchema.nullish().describe('Only room type requires metadata (selectedRateKey, occupancy)'),
  }),
  AddItineraryItemInputBaseSchema.extend({
    type: z.literal('custom'),
    title: z
      .string()
      .optional()
      .describe(
        'Optional title for the itinerary item; allowed only for custom items. When omitted the server generates a title.',
      ),
    referenceData: RecordUnknownSchema.optional(),
    metadata: RecordUnknownSchema.nullish().describe('Only room type requires metadata (selectedRateKey, occupancy)'),
  }),
  // Backward compatibility: existing Firestore docs may have type: 'activity'
  AddItineraryItemInputBaseSchema.extend({
    type: z.literal('activity'),
    title: z.string().optional(),
    referenceData: RecordUnknownSchema.optional(),
    metadata: RecordUnknownSchema.optional(),
  }),
  AddItineraryItemInputBaseSchema.extend({
    type: z.literal('place'),
    referenceData: RecordUnknownSchema.optional(),
    metadata: RecordUnknownSchema.optional().describe(
      'Optional; only room type requires metadata (selectedRateKey, occupancy)',
    ),
  }),
]);

/**
 * TypeScript types derived from schemas
 */
export type BookingDetails = z.infer<typeof BookingDetailsSchema>;
export type BookingTraveller = z.infer<typeof BookingTravellerSchema>;
export type ItineraryItemData = z.infer<typeof ItineraryItemSchema>;
export type Passenger = z.infer<typeof PassengerSchema>;
export type ExperienceDetails = z.infer<typeof ExperienceDetailsSchema>;

/**
 * Type for all valid itinerary item types
 */
export type ItineraryItemType = z.infer<typeof ItineraryItemBaseSchema>['type'];
